import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '../api/api';
import loadImage from '../assets/imageLoader';
import { FaUserTie, FaCheck, FaTimes, FaMapMarkerAlt, FaEnvelope, FaBuilding } from 'react-icons/fa';

function BrokerTeam() {
  const [invitations, setInvitations] = useState([]);
  const [currentBroker, setCurrentBroker] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchRosterStatus = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await apiClient.get('/worker/roster');
      setInvitations(response.data.invitations || []);
      setCurrentBroker(response.data.currentBroker);
    } catch (err) {
      console.error("Error fetching roster status:", err);
      setError('Failed to load agent team information.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRosterStatus();
  }, []);

  const handleRespond = async (rosterId, action) => {
    if (!window.confirm(`Are you sure you want to ${action} this invitation?`)) return;

    try {
      await apiClient.post('/worker/roster/respond', { rosterId, action });
      fetchRosterStatus(); 
    } catch (err) {
      alert(`Failed to ${action} invitation.`);
    }
  };

  if (loading) return (
      <div className="flex justify-center items-center h-screen bg-slate-50">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 p-6 lg:p-10 font-sans">
        <div className="max-w-5xl mx-auto">
            
            {/* Header */}
            <div className="mb-10">
                <h1 className="text-3xl font-black text-slate-800 tracking-tight">Agent's Team</h1>
                <p className="text-slate-500 mt-1">Manage your team memberships and invitations.</p>
            </div>

            {error && <div className="p-6 mb-8 bg-red-50 text-red-700 rounded-2xl font-medium">{error}</div>}

            <div className="grid grid-cols-1 gap-8">
                
                {/* Invitations Section */}
                {invitations.length > 0 && (
                    <div className="bg-white rounded-[2rem] p-8 shadow-sm border border-blue-100 relative overflow-hidden">
                        <div className="absolute top-0 left-0 w-2 h-full bg-blue-500"></div>
                        <h3 className="text-xl font-bold text-slate-800 mb-6 flex items-center">
                            <span className="bg-blue-100 text-blue-800 text-xs font-black px-3 py-1 rounded-full mr-3 uppercase tracking-wider">New Invite</span>
                            You have pending invitations
                        </h3>
                        
                        <div className="grid gap-4">
                            {invitations.map(invite => (
                                <div key={invite.id} className="flex flex-col md:flex-row justify-between items-center p-6 bg-slate-50 rounded-2xl border border-slate-100">
                                    <div className="flex items-center gap-5 mb-6 md:mb-0 w-full md:w-auto">
                                        <img 
                                            src={loadImage(invite.broker?.picture)} 
                                            alt={invite.broker?.name} 
                                            className="w-16 h-16 rounded-2xl object-cover shadow-sm bg-white"
                                        />
                                        <div>
                                            <h4 className="text-lg font-bold text-slate-800">{invite.broker?.name}</h4>
                                            <p className="text-slate-500 text-sm flex items-center mt-1">
                                                <FaMapMarkerAlt className="mr-1.5 text-blue-400" />
                                                {invite.broker?.location?.district || 'Location N/A'}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex gap-3 w-full md:w-auto">
                                        <button 
                                            onClick={() => handleRespond(invite.id, 'accept')}
                                            className="flex-1 md:flex-none px-6 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-all shadow-lg shadow-blue-200 flex items-center justify-center"
                                        >
                                            <FaCheck className="mr-2" /> Join Team
                                        </button>
                                        <button 
                                            onClick={() => handleRespond(invite.id, 'decline')}
                                            className="flex-1 md:flex-none px-6 py-3 bg-white border border-slate-200 text-slate-600 rounded-xl font-bold hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-all flex items-center justify-center"
                                        >
                                            <FaTimes className="mr-2" /> Decline
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Current Agent Status */}
                {currentBroker ? (
                    <div className="bg-white rounded-[2rem] shadow-lg border border-slate-100 overflow-hidden relative">
                        <div className="h-24 bg-gradient-to-r from-slate-800 to-slate-700"></div>
                        <div className="px-8 pb-8">
                            <div className="flex flex-col md:flex-row items-center md:items-end -mt-12 gap-6">
                                <img 
                                    src={loadImage(currentBroker.picture)} 
                                    alt={currentBroker.name} 
                                    className="w-32 h-32 rounded-3xl object-cover border-4 border-white shadow-xl bg-white"
                                />
                                <div className="flex-grow text-center md:text-left mb-2">
                                    <span className="inline-block px-3 py-1 bg-emerald-100 text-emerald-800 text-[10px] font-black uppercase tracking-widest rounded-full mb-2">
                                        Active Agent
                                    </span>
                                    <h2 className="text-3xl font-black text-slate-800">{currentBroker.name}</h2>
                                    <p className="text-slate-500 font-medium mt-1 flex items-center justify-center md:justify-start">
                                        <FaBuilding className="mr-2 text-slate-400" />
                                        {currentBroker.location?.district || 'Location N/A'}
                                    </p>
                                </div>
                                <div className="flex gap-3 w-full md:w-auto">
                                    <Link 
                                        to={`/messages`} // Link to chat with broker
                                        state={{ startChatWith: { id: currentBroker.user_id, name: currentBroker.name } }}
                                        className="flex-1 md:flex-none px-6 py-3 bg-emerald-600 text-white rounded-xl font-bold hover:bg-emerald-700 transition-all shadow-lg shadow-emerald-200 flex items-center justify-center"
                                    >
                                        <FaEnvelope className="mr-2" /> Message
                                    </Link>
                                    <Link 
                                        to={`/profile/${currentBroker.user_id}`}
                                        className="flex-1 md:flex-none px-6 py-3 bg-white border border-slate-200 text-slate-600 rounded-xl font-bold hover:bg-slate-50 transition-all flex items-center justify-center"
                                    >
                                        View Profile
                                    </Link>
                                </div>
                            </div>
                            
                            <div className="mt-8 p-6 bg-slate-50 rounded-2xl border border-slate-100">
                                <h4 className="font-bold text-slate-800 mb-2">Team Status</h4>
                                <p className="text-slate-600 text-sm leading-relaxed">
                                    You are an active member of <strong>{currentBroker.name}'s</strong> team. They can assign you to exclusive jobs and manage your schedule. Contact your agent for work updates.
                                </p>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="bg-white rounded-[2rem] p-10 text-center border-2 border-dashed border-slate-200">
                        <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6 text-slate-300">
                            <FaUserTie className="text-4xl" />
                        </div>
                        <h3 className="text-2xl font-black text-slate-800 mb-2">You are a Free Agent</h3>
                        <p className="text-slate-500 max-w-lg mx-auto mb-8">
                            You are not currently part of any agent's team. You can continue to work independently, or wait for an agent to invite you to join their roster for more opportunities.
                        </p>
                        <div className="inline-block bg-slate-50 px-6 py-3 rounded-xl text-slate-400 font-bold text-sm">
                            Waiting for invitations...
                        </div>
                    </div>
                )}

            </div>
        </div>
    </div>
  );
}

export default BrokerTeam;